import { ColumnPinningState } from '@tanstack/react-table';
import { useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { useEffect, useMemo, useRef } from 'react';
import {
  fetchColumnOrder,
  fetchColumnPinning,
  insertColumnsAtCorrectPosition,
  mergeColumnOrder,
  updateColumnOrder,
  updateColumnPinning,
} from 'utils/tables';

interface UseColumnStateProps {
  tableId: string;
  initialColumnOrder: string[];
  initialColumnPinning: ColumnPinningState;
}

export function useColumnState({
  tableId,
  initialColumnOrder,
  initialColumnPinning,
}: UseColumnStateProps) {
  const columnOrderAtom = useMemo(
    () =>
      atomWithStorage<string[]>(
        tableId,
        initialColumnOrder,
        {
          getItem: (key: string) => {
            const fetchedOrder = fetchColumnOrder(key);
            if (!fetchedOrder || fetchedOrder.length === 0) {
              return initialColumnOrder;
            }
            // Insert any new columns at their correct position based on initialColumnOrder.
            // This ensures new columns appear in their logical position (e.g., after "Total")
            // rather than being appended at the end when users have customized column order.
            const missingColumns = initialColumnOrder.filter(
              (col) => !fetchedOrder.includes(col)
            );
            return insertColumnsAtCorrectPosition(
              fetchedOrder,
              initialColumnOrder,
              missingColumns
            );
          },
          setItem: (key: string, newValue: string[]) =>
            updateColumnOrder(key, newValue),
          removeItem: () => void 0,
        },
        { getOnInit: true }
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tableId]
  );

  const columnPinningAtom = useMemo(
    () =>
      atomWithStorage<ColumnPinningState>(
        tableId,
        initialColumnPinning,
        {
          getItem: (key: string) => {
            const fetchPinnedColumns = fetchColumnPinning(key);
            return fetchPinnedColumns?.left &&
              fetchPinnedColumns?.left.length > 0
              ? fetchPinnedColumns
              : initialColumnPinning;
          },
          setItem: (key: string, newValue: ColumnPinningState) =>
            updateColumnPinning(key, newValue),
          removeItem: () => void 0,
        },
        { getOnInit: true }
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tableId]
  );

  const [columnOrder, setColumnOrder] = useAtom(columnOrderAtom);
  const [columnPinning, setColumnPinning] = useAtom(columnPinningAtom);

  // Track previous initialColumnOrder to detect dynamic column changes
  const prevInitialColumnOrderRef = useRef<string[]>(initialColumnOrder);

  // Handle dynamic column changes (e.g., when toggles add/remove columns like "Expired Permit").
  // This ensures the persisted column order stays in sync when columns are dynamically shown/hidden.
  useEffect(() => {
    const prevInitialOrder = prevInitialColumnOrderRef.current;

    const newColumnOrder = mergeColumnOrder(
      columnOrder,
      initialColumnOrder,
      prevInitialOrder
    );

    if (newColumnOrder !== columnOrder) {
      setColumnOrder(newColumnOrder);
    }

    prevInitialColumnOrderRef.current = initialColumnOrder;
  }, [initialColumnOrder, columnOrder, setColumnOrder]);

  return {
    columnOrder,
    setColumnOrder,
    columnPinning,
    setColumnPinning,
  };
}
